import React from 'react';

const CustomAlert = ({ 
  isOpen, 
  onClose, 
  title = "Alert", 
  message, 
  type = "info", // info, success, warning, error
  confirmText = "OK",
  cancelText = "Cancel",
  onConfirm,
  showCancel = false 
}) => {
  if (!isOpen) return null;

  const getAlertStyles = () => {
    switch (type) {
      case 'success':
        return {
          bgGradient: 'from-emerald-400 via-emerald-500 to-teal-500',
          iconBg: 'bg-white/90 backdrop-blur-sm',
          iconColor: 'text-emerald-600',
          icon: 'fas fa-check',
          buttonBg: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500',
          buttonShadow: 'shadow-emerald-500/25',
          accentColor: 'emerald'
        };
      case 'error':
        return {
          bgGradient: 'from-red-400 via-red-500 to-rose-500',
          iconBg: 'bg-white/90 backdrop-blur-sm',
          iconColor: 'text-red-600',
          icon: 'fas fa-times',
          buttonBg: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          buttonShadow: 'shadow-red-500/25',
          accentColor: 'red'
        };
      case 'warning':
        return {
          bgGradient: 'from-amber-400 via-orange-400 to-yellow-500',
          iconBg: 'bg-white/90 backdrop-blur-sm',
          iconColor: 'text-amber-600',
          icon: 'fas fa-exclamation',
          buttonBg: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
          buttonShadow: 'shadow-amber-500/25',
          accentColor: 'amber'
        };
      default:
        return {
          bgGradient: 'from-blue-400 via-blue-500 to-indigo-500',
          iconBg: 'bg-white/90 backdrop-blur-sm',
          iconColor: 'text-blue-600',
          icon: 'fas fa-info',
          buttonBg: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          buttonShadow: 'shadow-blue-500/25',
          accentColor: 'blue'
        };
    }
  };

  const styles = getAlertStyles();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-md w-full transform transition-all duration-500 ease-out animate-slideUp border border-white/20">
        
        {/* Modern floating header */}
        <div className={`bg-gradient-to-br ${styles.bgGradient} p-8 rounded-t-3xl relative overflow-hidden`}>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
          
          <div className="relative z-10 flex flex-col items-center">
            <div className={`w-20 h-20 ${styles.iconBg} rounded-2xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform duration-300`}>
              <i className={`${styles.icon} ${styles.iconColor} text-2xl`}></i>
            </div>
            <h2 className="text-2xl font-bold text-white mt-6 text-center tracking-tight drop-shadow-sm">
              {title}
            </h2>
          </div>
        </div>

        {/* Content with glass effect */}
        <div className="p-8 bg-gradient-to-b from-white/80 to-white/60 backdrop-blur-sm">
          <p className="text-gray-700 text-center leading-relaxed text-lg font-medium">
            {message}
          </p>
        </div>

        {/* Modern action buttons */}
        <div className="px-8 pb-8 bg-gradient-to-b from-white/60 to-white/80 backdrop-blur-sm rounded-b-3xl">
          <div className="flex gap-4 justify-center">
            {showCancel && (
              <button
                onClick={onClose}
                className="px-8 py-4 bg-white/80 backdrop-blur-sm border border-gray-200/50 text-gray-700 rounded-2xl hover:bg-white hover:scale-105 transition-all duration-300 font-semibold min-w-[120px] shadow-lg hover:shadow-xl"
              >
                {cancelText}
              </button>
            )}
            <button
              onClick={handleConfirm}
              className={`px-8 py-4 ${styles.buttonBg} text-white rounded-2xl transition-all duration-300 font-semibold min-w-[120px] shadow-xl ${styles.buttonShadow} hover:shadow-2xl hover:scale-105 focus:outline-none focus:ring-4 focus:ring-opacity-50 transform active:scale-95`}
            >
              {confirmText}
            </button>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 group border border-white/10"
        >
          <i className="fas fa-times text-white text-sm group-hover:scale-110 transition-transform"></i>
        </button>

        {/* Floating particles effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white/20 rounded-full animate-float-1"></div>
          <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-white/15 rounded-full animate-float-2"></div>
          <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-white/25 rounded-full animate-float-3"></div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(100px) scale(0.8) rotateX(20deg);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1) rotateX(0deg);
          }
        }
        
        @keyframes float-1 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes float-2 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(-180deg); }
        }
        
        @keyframes float-3 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(360deg); }
        }
        
        .animate-slideUp {
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-float-1 {
          animation: float-1 4s ease-in-out infinite;
        }
        
        .animate-float-2 {
          animation: float-2 5s ease-in-out infinite 1s;
        }
        
        .animate-float-3 {
          animation: float-3 3s ease-in-out infinite 2s;
        }
      `}</style>
    </div>
  );
};

export default CustomAlert;